import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import { downloadApiExport, downloadFile, getDateForFileName } from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import { EPaymentStatus } from "types";

import type { PaymentStatus, TFetchStatus, TParamsPagination } from "./types";

/**
 * Used to get all payments between two dates.
 */
export const fetchPaymentsBetweenDates = createAsyncThunk(
    "payment/fetchPaymentsBetweenDates",
    async (
        {
            startDate,
            endDate,
            paymentLotId,
            forURSSAF = "false",
            ...paramsPagination
        }: {
            startDate: string;
            endDate: string;
            paymentLotId?: number | undefined;
            forURSSAF?: "true" | "false";
        } & TParamsPagination,
        { rejectWithValue },
    ) => {
        try {
            const {
                data: { data },
            } = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/payment", {
                params: {
                    startDate,
                    endDate,
                    paymentLotId,
                    forURSSAF,
                    ...paramsPagination,
                },
            });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Fetches all payments for history tab with pagination.
 */
export const fetchPaymentsHistory = createAsyncThunk(
    "payments/fetchPaymentsHistory",
    async (
        {
            startDate,
            endDate,
            paymentLotId,
            options,
        }: { options: TParamsPagination; startDate?: string; endDate?: string; paymentLotId?: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/payment/history", {
                params: { startDate, endDate, paymentLotId, options },
            });
            return response.data.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Gets payments linked to a resource id / type.
 */
export const fetchResourcePayment = createAsyncThunk(
    "payments/fetchResourcePayment",
    async (
        { relatedResourceId, resourceType }: { relatedResourceId: number; resourceType: EPaymentResourceType },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/payment/resource/${relatedResourceId}`,
                { params: { resourceType } },
            );
            return response.data.data.result;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    },
);

export const fetchExportFile = createAsyncThunk(
    "payment/fetchExportFile",
    async (
        {
            params,
            downloadMode,
        }: {
            params: {
                startDate: string;
                endDate: string;
                paymentLotId?: number | null;
                formatId?: string;
                fileNumber?: number;
            };
            downloadMode: "preview" | "pay";
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export?${downloadMode}=true`, {
                    params,
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `${t("Export.BankFileName")}_${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchURSSAFFile = createAsyncThunk(
    "payment/fetchExportUrssaf",
    async (
        params: { startDate: string; endDate: string; format?: string; periodChosen?: string; contributionId?: number },
        { rejectWithValue },
    ) => {
        try {
            // the format is optional, if not provided, it will default to "txt"
            params.format = params.format ?? "txt";

            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/payment/export/contributions", {
                    params: params,
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `export_urssaf_${params.periodChosen}.${params.format === "txt" ? "dat" : "xlsx"}`;

            if (params.format === "txt") {
                downloadFile({ source: response.data, fileName });
            } else {
                // the response.blob is defined in the axios response type
                const blob = response.data as Blob;
                downloadFile({ source: blob, fileName });
            }
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchURSSAFErrors = createAsyncThunk(
    "payment/fetchURSSAFErrors",
    async (params: { startDate: string; endDate: string; downloadMode: boolean }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/payment/export/contributions/verify",
                { params },
            );

            if (params.downloadMode === true) {
                const fileName = `export_anomalies_urssaf_${getDateForFileName()}.xlsx`;
                const linkSource = `data:application/vnd.openxmlformats;base64,${response.data}`;
                downloadFile({ source: linkSource, fileName, isLink: true });
            }

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data.data);
        }
    },
);

export const fetchDAS2Export = createAsyncThunk(
    "payment/fetchExportDas2",
    async (params: { startDate: string; endDate: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/das2`, {
                    params,
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `export_das2_${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAccountingExport = createAsyncThunk(
    "payment/fetchExportAccounting",
    async (params: { startDate: string; endDate: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/accounting`, {
                    params,
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `${t("Export.AccountingFileName")}_${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Accounting projection for the "Paiements pour virement" list: unpaid payments, no beneficiary filter.
// Backend streams a single xlsx when no specialized format file is generated (pennylane/lunii/default),
// otherwise a ZIP with the specialized file + xlsx.
export const fetchAccountingPreviewExport = createAsyncThunk(
    "payment/fetchAccountingPreviewExport",
    async (params: { startDate: string; endDate: string; hasSpecializedFile: boolean }, { rejectWithValue }) => {
        const { hasSpecializedFile, ...queryParams } = params;
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/accounting`, {
                    params: { ...queryParams, preview: "true" },
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const extension = hasSpecializedFile ? "zip" : "xlsx";
            const fileName = `${t("Export.AccountingFileName")}_${getDateForFileName()}.${extension}`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTaxDeclarationExport = createAsyncThunk(
    "payment/fetchTaxDeclarationExport",
    async (params: { startDate: string; endDate: string; beneficiaryIds?: number[] }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/tax-declaration`, {
                    params: {
                        startDate: params.startDate,
                        endDate: params.endDate,
                        ...(params.beneficiaryIds?.length ? { beneficiaryIds: params.beneficiaryIds.join(",") } : {}),
                    },
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `paiements_declaration_fiscale_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Uploads Sage accounting file to S3 for SFTP transfer.
 */
export const uploadSageAccountingToSftp = createAsyncThunk(
    "payment/uploadSageAccountingToSftp",
    async (
        params: {
            startDate: string;
            endDate: string;
            executionDate: string;
            paymentLotId?: number | null;
            formatId?: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/sage-accounting`, null, {
                    params,
                }),
                {
                    pending: translationsWithFallback("PaymentModal.SageUploadPending", "Transmission en cours..."),
                    success: translationsWithFallback("PaymentModal.SageUploadSuccess", "Fichier transmis avec succès"),
                    error: translationsWithFallback("PaymentModal.SageUploadError", "Erreur lors de la transmission"),
                },
            );

            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.message);
        }
    },
);

/**
 * Fetches the history of Sage accounting files uploaded to SFTP outbound.
 */
export const fetchSageAccountingUploadHistory = createAsyncThunk(
    "payment/fetchSageAccountingUploadHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/sage-accounting/history`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.message);
        }
    },
);

/**
 * Downloads a Sage accounting file from S3 by its record ID.
 */
export const downloadSageAccountingFile = createAsyncThunk(
    "payment/downloadSageAccountingFile",
    async ({ id, fileName }: { id: number; fileName: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/payment/export/sage-accounting/file/${id}`,
                {
                    responseType: "blob",
                },
            );
            downloadFile({ source: response.data, fileName });
            return { id, fileName };
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.message);
        }
    },
);

export const fetchPaymentBeneficiary = createAsyncThunk(
    "payment/fetchPaymentBeneficiary",
    async (
        { id, ...params }: { startDate: string; endDate: string; id: number; paymentLotId: number | null },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/beneficiary/${id}`, {
                params,
            });
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updatePaymentStatus = createAsyncThunk(
    "payment/updatePaymentStatus",
    async (
        { termId, paidStatus, periodId }: { termId: number; paidStatus: number; periodId: number },
        { rejectWithValue },
    ) => {
        try {
            await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/paymentStatus`, {
                paidStatus,
                periodId,
            });
            return { termId, paidStatus, periodId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateAdvancePaymentStatus = createAsyncThunk(
    "payment/updateAdvancePaymentStatus",
    async ({ paymentSliceId, paidStatus }: { paymentSliceId: number; paidStatus: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/advance/slice/${paymentSliceId}`,
                { status: paidStatus },
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

export const updatePaymentDate = createAsyncThunk(
    "payment/updatePaymentDate",
    async (
        { termId, paymentDate, periodId }: { termId: number; paymentDate: Date; periodId: number },
        { rejectWithValue },
    ) => {
        try {
            await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/paymentStatus`, {
                paymentDate,
                periodId,
            });
            return { termId, paymentDate, periodId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateAdvancePaymentDate = createAsyncThunk(
    "payment/updateAdvancePaymentDate",
    async ({ paymentSliceId, paymentDate }: { paymentSliceId: number; paymentDate: Date }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/advance/slice/${paymentSliceId}`,
                { paymentDate },
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

/**
 * Updates a payment lot.
 */
export const updatePaymentLot = createAsyncThunk(
    "payment/updatePaymentLot",
    async (
        {
            paymentLotId,
            paymentDate,
            status,
        }: { paymentLotId: number; paymentDate?: Date | null; status?: "cancelled" },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.patch(
                import.meta.env.REACT_APP_API_ENDPOINT + `/payment/lot/${paymentLotId}`,
                { paymentDate, status },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Downloads again bank payment file in a ZIP
 */
export const downloadLotBankFile = createAsyncThunk(
    "payment/downloadLotBankFile",
    async ({ paymentLotId }: { paymentLotId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/lot/${paymentLotId}/bank`, {
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `${t("Export.BankFileName")}_${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Downloads accounting payment file in a ZIP
 */
export const downloadLotAccountingFile = createAsyncThunk(
    "payment/downloadLotAccountingFile",
    async ({ paymentLotId }: { paymentLotId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/lot/${paymentLotId}/accounting`, {
                    headers: { Accept: "*/*" },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `${t("Export.AccountingFileName")}_${getDateForFileName()}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type TPaymentDatesBounds = {
    oldestPaymentDate: string | null;
    newestPaymentDate: string | null;
};

/** Min/max payment dates (PAID / TO_PAY lots); replaces separate /oldest call on Payment page. */
export const getPaymentDatesBounds = createAsyncThunk(
    "payment/getPaymentDatesBounds",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/payment/dates-bounds`);
            return response.data.data as TPaymentDatesBounds;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export enum EPaymentResourceType {
    BENEFICIARY = "beneficiary",
    ARTICLE = "article",
    ADVANCE_PAYMENT = "advance_payment",
    PARTIAL_PAYMENT = "partial_payment",
}

export interface IPaymentResource {
    type: EPaymentResourceType;
    id: number;
    amount: number;
    periodId?: number;
}

export enum EPaymentType {
    MANUAL = "manual",
    GROUPED = "grouped",
}

/**
 * Creates a line of payment (to appear in the Payment page)
 */
export const addToPayment = createAsyncThunk(
    "payment/addToPayment",
    async (
        { data }: { data: { method: EPaymentType; paymentDate: Date; resources: IPaymentResource[] } },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/payment", { ...data });
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

/**
 * Removes a lot and its associated payments
 */
export const eraseLot = createAsyncThunk(
    "payment/eraseLot",
    async ({ paymentLotId }: { paymentLotId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/payment/lot/${paymentLotId}`,
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

/**
 * Removes a beneficiary from payment (so beneficiary won't be in lot anymore)
 */
export const removeBeneficiaryFromLot = createAsyncThunk(
    "payment/removeBeneficiaryFromLot",
    async ({ paymentLotId, beneficiaryId }: { paymentLotId: number; beneficiaryId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/payment/lot/${paymentLotId}/beneficiary/${beneficiaryId}`,
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

/**
 * Removes a line from payment (so removes a payment type from beneficiary payments)
 */
export const removeTypeFromPayment = createAsyncThunk(
    "payment/removeTypeFromPayment",
    async (
        {
            hasOnlyOnePayment,
            paymentLotId,
            beneficiaryId,
            paymentType,
            periodId,
        }: {
            hasOnlyOnePayment: boolean;
            paymentLotId: number;
            beneficiaryId: number;
            paymentType: string | undefined;
            periodId: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/payment/lot/${paymentLotId}/beneficiary/${beneficiaryId}/${paymentType}`,
                { params: { periodId } },
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

export const getPaymentLots = createAsyncThunk(
    "payment/getPaymentLots",
    async (options: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/payment/lot/lots", {
                params: options,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export interface IPaymentResourceInfo {
    id?: number;
    netToPay?: number;
    titleName?: string;
    titleAvatarUrl?: string;
    isResourceDeleted?: boolean;
    accountingPieceNumber?: string | null;
}

export interface IPaymentInfo {
    id: number;
    name: string;
    totalValue: null | number;
    type: number;
    isForfait?: boolean;
    termId: number;
    periodId: number;
    periodStart?: Date;
    periodEnd?: Date;
    paidStatus?: number;
    paymentSliceId?: number;
    beneficiarId: number;
    paymentDate?: Date;
    titleAvatarUrl?: string;
    titleId: number;
    titleName: string;
    deletedResources: number[];
    allResources: IPaymentResourceInfo[];
    netToPay?: number;
}

export interface IPayment {
    id: number;
    date: Date;
    grossValue: number;
    name: string;
    netValue: number;
    totalValue: number;
    bankInfoComplete?: number;
    socialIdComplete?: number;
    addressComplete?: number;
    netRemainingToPay: number;
    beneficiaryAvatarUrl?: string;
    acceptedPayments?: string;
    iban?: string;
    paypalEmail?: string;
    bankCheck?: number;
    contributions: number;
    beneficiary: IBeneficiar;
    computeFromRepresentative: number;
    _representative: IRepresentative;
    paymentLotId: number | null;
    lotDeletedAt: string | null;
    paidStatus: number;
    isResourceDeleted: number;
    accountingPieceNumber?: string | null;
}

/**
 * Same as line within DB table "beneficiary_payments"
 */
export interface IBeneficiaryPayment {
    id: number;
    periodId: number | null;
    beneficiaryId: number;
    paymentDate: Date | null;
    amount: number;
    label: string | null;
    paymentLotId: number;
    snapshot: JSON;
    relatedResource: EPaymentResourceType;
    relatedResourceId: number;
    metadata: JSON;
    deletedAt: Date | null;
    companyId: number;
    status: PaymentStatus;
}

export interface IUrssafErrors {
    field: string;
    fileLine: number;
    line: IPayment;
    message: string;
}

export interface IPaymentLot {
    id: number;
    paymentDate: Date | null;
    status: PaymentStatus;
    method: "grouped" | "manual";
    beneficiariesName: string;
    paymentsCount: number;
    netTotal: number;
}

export interface TermState {
    payments: IPayment[];
    paymentInfo: IPaymentInfo[] | [];
    paymentsStatus: TFetchStatus;
    paymentInfoStatus: TFetchStatus;
    exportFileStatus: TFetchStatus;
    changeStatusPaymentStatus: TFetchStatus;
    changeStatusAdvancePaymentStatus: TFetchStatus;
    urssafErrors: IUrssafErrors[];
    urssafPayments: IPayment[];
    urssafErrorsStatus: TFetchStatus;
    lastPaymentFetchType: "payments" | "declarations" | "";
    paymentLots: IPaymentLot[];
    paymentLotsStatus: TFetchStatus;
    paymentLotsOptions: number[];
    paymentCounts: number;
    paginatedPayments: IPayment[];
    paginatedPaymentsCount: number;
    paginatedPaymentsStatus: TFetchStatus;
    resourcePayment: IBeneficiaryPayment;
    oldestPaymentMade: string | null;
    newestPaymentMade: string | null;
    paymentDatesBoundsStatus: TFetchStatus;
    sageUploadHistory: any[];
    sageUploadHistoryStatus: TFetchStatus;
}

const initialState: TermState = {
    exportFileStatus: "idle",
    payments: [],
    paymentInfo: [],
    paymentsStatus: "idle",
    paymentInfoStatus: "idle",
    changeStatusPaymentStatus: "idle",
    changeStatusAdvancePaymentStatus: "idle",
    urssafErrors: [] as IUrssafErrors[],
    urssafPayments: [] as IPayment[],
    urssafErrorsStatus: "idle",
    lastPaymentFetchType: "",
    paymentLots: [] as IPaymentLot[],
    paymentLotsStatus: "idle",
    paymentLotsOptions: [],
    paymentCounts: 0,
    paginatedPayments: [],
    paginatedPaymentsCount: 0,
    paginatedPaymentsStatus: "idle",
    resourcePayment: {} as IBeneficiaryPayment,
    oldestPaymentMade: "",
    newestPaymentMade: "",
    paymentDatesBoundsStatus: "idle",
    sageUploadHistory: [],
    sageUploadHistoryStatus: "idle",
};

export const paymentSlice = createSlice({
    name: "payment",
    initialState,
    reducers: {
        setExportFileIdleStatus(state) {
            state.exportFileStatus = "idle";
        },
        updatePaymentsFetchStatus(state, { payload }) {
            state.paymentsStatus = payload;
        },
        updatePaginatedPaymentsFetchStatus(state, { payload }) {
            state.paginatedPaymentsStatus = payload;
        },
        updateWaitingPayments(state, { payload }) {
            const waitingPayments = [...state.payments];
            state.payments = waitingPayments.filter((item) => item.paymentLotId !== payload);
        },
        resetPaymentInfo(state) {
            state.paymentInfo = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPaymentsBetweenDates.fulfilled, (state, { payload }) => {
            state.payments = payload.result;
            state.paginatedPayments = payload.result;
            state.paymentsStatus = "succeeded";
        });
        builder.addCase(fetchPaymentsBetweenDates.pending, (state) => {
            state.paymentsStatus = "pending";
        });
        builder.addCase(fetchPaymentsBetweenDates.rejected, (state, { payload }) => {
            state.paymentsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchPaymentBeneficiary.fulfilled, (state, action) => {
            state.paymentInfo = action.payload;
            state.paymentInfoStatus = "succeeded";
        });
        builder.addCase(fetchPaymentBeneficiary.pending, (state) => {
            state.paymentInfoStatus = "pending";
        });
        builder.addCase(fetchPaymentBeneficiary.rejected, (state, { payload }) => {
            state.paymentInfoStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchExportFile.pending, (state) => {
            state.exportFileStatus = "pending";
        });
        builder.addCase(fetchExportFile.fulfilled, (state, action) => {
            toast.success(`${t("Payment.SuccessExport")}`);
            state.exportFileStatus = "succeeded";

            const mode = action.meta.arg.downloadMode;
            const paymentLotId = action.meta.arg.params.paymentLotId;

            if (mode === "pay" && paymentLotId) {
                state.payments = [...state.payments].map((item) => {
                    if (item.paymentLotId === paymentLotId) {
                        return { ...item, date: new Date(), status: EPaymentStatus.PAID, netRemainingToPay: 0 };
                    } else {
                        return item;
                    }
                });
                state.paginatedPayments = [...state.paginatedPayments].map((item) => {
                    if (item.paymentLotId === paymentLotId) {
                        return { ...item, date: new Date(), status: EPaymentStatus.PAID, netRemainingToPay: 0 };
                    } else {
                        return item;
                    }
                });
            }
        });
        builder.addCase(fetchExportFile.rejected, (state, { payload }) => {
            state.exportFileStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchURSSAFFile.pending, (state) => {
            state.exportFileStatus = "pending";
        });
        builder.addCase(fetchURSSAFFile.fulfilled, (state) => {
            toast.success(`${t("Payment.SuccessExport")}`);
            state.exportFileStatus = "succeeded";
        });
        builder.addCase(fetchURSSAFFile.rejected, (state, { payload }) => {
            state.exportFileStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchURSSAFErrors.rejected, (state, action) => {
            if (!action.meta.arg.downloadMode) {
                state.urssafErrors = (action.payload as any).errors;
                state.urssafPayments = (action.payload as any).payments;
                state.urssafErrorsStatus = "failed";
            }
        });
        builder.addCase(fetchURSSAFErrors.fulfilled, (state, action) => {
            if (!action.meta.arg.downloadMode) {
                state.urssafErrorsStatus = "succeeded";
                state.urssafErrors = [];
                state.urssafPayments = [];
            }
        });
        builder.addCase(fetchURSSAFErrors.pending, (state, action) => {
            if (!action.meta.arg.downloadMode) {
                state.urssafErrorsStatus = "pending";
                state.urssafErrors = [];
                state.urssafPayments = [];
            }
        });
        builder.addCase(fetchDAS2Export.pending, (state) => {
            state.exportFileStatus = "pending";
        });
        builder.addCase(fetchDAS2Export.fulfilled, (state) => {
            state.exportFileStatus = "succeeded";
        });
        builder.addCase(fetchDAS2Export.rejected, (state) => {
            state.exportFileStatus = "failed";
        });

        builder.addCase(fetchAccountingExport.pending, (state) => {
            state.exportFileStatus = "pending";
        });
        builder.addCase(fetchAccountingExport.fulfilled, (state) => {
            state.exportFileStatus = "succeeded";
        });
        builder.addCase(fetchAccountingExport.rejected, (state) => {
            state.exportFileStatus = "failed";
        });

        builder.addCase(fetchTaxDeclarationExport.pending, (state) => {
            state.exportFileStatus = "pending";
        });
        builder.addCase(fetchTaxDeclarationExport.fulfilled, (state) => {
            state.exportFileStatus = "succeeded";
        });
        builder.addCase(fetchTaxDeclarationExport.rejected, (state) => {
            state.exportFileStatus = "failed";
        });

        builder.addCase(updatePaymentStatus.pending, (state) => {
            state.changeStatusPaymentStatus = "pending";
        });
        builder.addCase(updatePaymentStatus.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullChangePaymentStatus")}`);
            const currentPaymentInfo = JSON.parse(JSON.stringify(state.paymentInfo));
            const index = currentPaymentInfo.findIndex(
                (item: IPaymentInfo) =>
                    item.termId === action.payload.termId && item.periodId === action.payload.periodId,
            );
            currentPaymentInfo[index].paidStatus = action.payload.paidStatus;
            state.paymentInfo = currentPaymentInfo;
            state.changeStatusPaymentStatus = "succeeded";
        });
        builder.addCase(updatePaymentStatus.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.changeStatusPaymentStatus = "failed";
        });

        builder.addCase(updatePaymentDate.pending, (state) => {
            state.changeStatusPaymentStatus = "pending";
        });
        builder.addCase(updatePaymentDate.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullChangePaymentDate")}`);
            const currentPaymentInfo = JSON.parse(JSON.stringify(state.paymentInfo));
            const index = currentPaymentInfo.findIndex(
                (item: IPaymentInfo) =>
                    item.termId === action.payload.termId && item.periodId === action.payload.periodId,
            );
            currentPaymentInfo[index].paymentDate = action.payload.paymentDate;
            state.paymentInfo = currentPaymentInfo;
            state.changeStatusPaymentStatus = "succeeded";
        });
        builder.addCase(updatePaymentDate.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.changeStatusPaymentStatus = "failed";
        });

        builder.addCase(updateAdvancePaymentStatus.pending, (state) => {
            state.changeStatusAdvancePaymentStatus = "pending";
        });
        builder.addCase(updateAdvancePaymentStatus.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullChangePaymentStatus")}`);
            state.paymentInfo = state.paymentInfo.map((payment) => {
                if (payment.paymentSliceId === action.meta.arg.paymentSliceId) {
                    payment.paidStatus = action.meta.arg.paidStatus;
                }
                return payment;
            });
            state.changeStatusAdvancePaymentStatus = "succeeded";
        });
        builder.addCase(updateAdvancePaymentStatus.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.changeStatusAdvancePaymentStatus = "failed";
        });

        builder.addCase(updateAdvancePaymentDate.pending, (state) => {
            state.changeStatusAdvancePaymentStatus = "pending";
        });
        builder.addCase(updateAdvancePaymentDate.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullChangePaymentDate")}`);
            state.paymentInfo = state.paymentInfo.map((payment) => {
                if (payment.paymentSliceId === action.meta.arg.paymentSliceId) {
                    payment.paymentDate = action.meta.arg.paymentDate;
                }
                return payment;
            });
            state.changeStatusAdvancePaymentStatus = "succeeded";
        });
        builder.addCase(updateAdvancePaymentDate.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.changeStatusAdvancePaymentStatus = "failed";
        });
        builder.addCase(addToPayment.fulfilled, () => {
            toast.success(`${t("PartialPayment.PaymentSuccessful")}`);
        });
        builder.addCase(addToPayment.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(updatePaymentLot.fulfilled, (state, action) => {
            const paymentLotId = action.meta.arg.paymentLotId;
            const status = action.meta.arg.status === "cancelled" ? 0 : undefined;
            const date = status === 0 ? new Date() : action.payload.paymentDate;

            const payments = [...state.payments].map((item: IPayment) => {
                if (item.paymentLotId && +item.paymentLotId === +paymentLotId) {
                    return { ...item, date, paidStatus: status ?? item.paidStatus };
                } else {
                    return item;
                }
            });
            state.payments = payments;

            const paginatedPayments = [...state.paginatedPayments].map((item: IPayment) => {
                if (item.paymentLotId && +item.paymentLotId === +paymentLotId) {
                    return { ...item, date, paidStatus: status ?? item.paidStatus };
                } else {
                    return item;
                }
            });
            state.paginatedPayments = paginatedPayments;

            const lots = [...state.paymentLots].map((item: IPaymentLot) => {
                if (item.id === paymentLotId) {
                    return { ...item, paymentDate: date, status: status ?? item.status };
                } else {
                    return item;
                }
            });
            state.paymentLots = lots;
            toast.success(`${t("PartialPayment.Slice.UpdateSuccess")}`);
        });
        builder.addCase(updatePaymentLot.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(eraseLot.fulfilled, (state, action) => {
            const paymentLotId = action.meta.arg.paymentLotId;
            state.payments = [...state.payments].filter((item) => item.paymentLotId !== paymentLotId);
            state.paginatedPayments = [...state.paginatedPayments].filter((item) => item.paymentLotId !== paymentLotId);
            state.paymentLots = [...state.paymentLots].filter((item) => item.id !== paymentLotId);
            toast.success(`Lot supprimé !`);
        });
        builder.addCase(eraseLot.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(removeBeneficiaryFromLot.fulfilled, (state, action) => {
            const paymentLotId = action.meta.arg.paymentLotId;
            const beneficiaryId = action.meta.arg.beneficiaryId;
            state.payments = [...state.payments].filter((item) => {
                if (item.paymentLotId === paymentLotId) {
                    return item.beneficiary.id !== beneficiaryId;
                } else {
                    return true;
                }
            });
            state.paginatedPayments = [...state.paginatedPayments].filter((item) => {
                if (item.paymentLotId === paymentLotId) {
                    return item.beneficiary.id !== beneficiaryId;
                } else {
                    return true;
                }
            });
            toast.success(`${t("PartialPayment.Slice.DeleteSuccess")}`);
        });
        builder.addCase(removeBeneficiaryFromLot.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(removeTypeFromPayment.fulfilled, (state, action) => {
            const beneficiaryId = action.meta.arg.beneficiaryId;
            const paymentType = action.meta.arg.paymentType;
            const hasOnlyOnePayment = action.meta.arg.hasOnlyOnePayment;

            state.paymentInfo = [...state.paymentInfo].filter((item) => {
                if (item.beneficiarId === beneficiaryId) {
                    // Not sure why but the actualPaymentType is in name. Type has its numeric value.
                    return item.name !== (paymentType === "beneficiary" ? "term" : paymentType);
                } else {
                    return true;
                }
            });

            if (hasOnlyOnePayment) {
                state.payments = [...state.payments].filter((item) => item.beneficiary.id !== beneficiaryId);
                state.paginatedPayments = [...state.paginatedPayments].filter(
                    (item) => item.beneficiary.id !== beneficiaryId,
                );
            }

            toast.success(`${t("PartialPayment.Slice.DeleteSuccess")}`);
        });
        builder.addCase(removeTypeFromPayment.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getPaymentLots.fulfilled, (state, { payload }) => {
            state.paymentLots = payload.lots;
            state.paymentLotsOptions = payload.lotOptions;
            state.paymentLotsStatus = "fulfilled";
        });
        builder.addCase(getPaymentLots.pending, (state) => {
            state.paymentLotsStatus = "pending";
        });
        builder.addCase(getPaymentLots.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.paymentLotsStatus = "rejected";
        });
        builder.addCase(fetchPaymentsHistory.fulfilled, (state, { payload }) => {
            state.paginatedPayments = payload.payments;
            state.paginatedPaymentsCount = payload.count;
            state.paginatedPaymentsStatus = "succeeded";
        });
        builder.addCase(fetchPaymentsHistory.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.paginatedPaymentsStatus = "rejected";
        });
        builder.addCase(fetchPaymentsHistory.pending, (state) => {
            state.paginatedPaymentsStatus = "pending";
        });
        builder.addCase(fetchResourcePayment.fulfilled, (state, { payload }) => {
            state.resourcePayment = payload;
        });
        builder.addCase(getPaymentDatesBounds.pending, (state) => {
            state.paymentDatesBoundsStatus = "pending";
        });
        builder.addCase(getPaymentDatesBounds.fulfilled, (state, { payload }) => {
            state.paymentDatesBoundsStatus = "succeeded";
            state.oldestPaymentMade = payload.oldestPaymentDate ?? "";
            state.newestPaymentMade = payload.newestPaymentDate ?? "";
        });
        builder.addCase(getPaymentDatesBounds.rejected, (state) => {
            state.paymentDatesBoundsStatus = "failed";
        });
        builder.addCase(fetchSageAccountingUploadHistory.fulfilled, (state, { payload }) => {
            state.sageUploadHistory = payload;
            state.sageUploadHistoryStatus = "fulfilled";
        });
        builder.addCase(fetchSageAccountingUploadHistory.pending, (state) => {
            state.sageUploadHistoryStatus = "pending";
        });
        builder.addCase(fetchSageAccountingUploadHistory.rejected, (state) => {
            state.sageUploadHistoryStatus = "failed";
        });
        builder.addCase(downloadSageAccountingFile.fulfilled, () => {
            // File download handled by downloadFile utility
        });
        builder.addCase(downloadSageAccountingFile.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});
export const {
    setExportFileIdleStatus,
    updatePaymentsFetchStatus,
    updatePaginatedPaymentsFetchStatus,
    updateWaitingPayments,
    resetPaymentInfo,
} = paymentSlice.actions;

// Selectors
export const selectLastUploadedSageFile = (state: { payment: TermState }) => {
    const { sageUploadHistory } = state.payment;
    return sageUploadHistory && sageUploadHistory.length > 0 ? sageUploadHistory[0] : null;
};

export default paymentSlice.reducer;
